import { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, BookOpen, Download, ShoppingBag, Users,
  Plus, Pencil, Trash2, DollarSign, TrendingUp, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { getAuthHeaders } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({ courses: [], downloads: [] });
  
  // Form states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productType, setProductType] = useState('course');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    thumbnail: '',
    video_url: '',
    file_url: '',
    file_type: '',
    duration: '',
    is_featured: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, productsRes, ordersRes, usersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/categories`)
      ]);
      setAnalytics(analyticsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const endpoint = productType === 'course' ? '/admin/courses' : '/admin/downloads';
      const payload = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        thumbnail: formData.thumbnail,
        is_featured: formData.is_featured
      };

      if (productType === 'course') {
        payload.video_url = formData.video_url;
        payload.duration = formData.duration;
      } else {
        payload.file_url = formData.file_url;
        payload.file_type = formData.file_type;
      }

      await axios.post(`${API}${endpoint}`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Product created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleUpdateProduct = async () => {
    try {
      const endpoint = editingProduct.product_type === 'course' 
        ? `/admin/courses/${editingProduct.product_id}` 
        : `/admin/downloads/${editingProduct.product_id}`;
      
      const payload = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        thumbnail: formData.thumbnail,
        is_featured: formData.is_featured
      };

      if (editingProduct.product_type === 'course') {
        payload.video_url = formData.video_url;
        payload.duration = formData.duration;
      } else {
        payload.file_url = formData.file_url;
        payload.file_type = formData.file_type;
      }

      await axios.put(`${API}${endpoint}`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Product updated successfully!');
      setIsDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      const endpoint = product.product_type === 'course' 
        ? `/admin/courses/${product.product_id}` 
        : `/admin/downloads/${product.product_id}`;
      
      await axios.delete(`${API}${endpoint}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Product deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductType(product.product_type);
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      thumbnail: product.thumbnail || '',
      video_url: product.video_url || '',
      file_url: product.file_url || '',
      file_type: product.file_type || '',
      duration: product.duration || '',
      is_featured: product.is_featured
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      category: '',
      thumbnail: '',
      video_url: '',
      file_url: '',
      file_type: '',
      duration: '',
      is_featured: false
    });
    setEditingProduct(null);
    setProductType('course');
  };

  const courses = products.filter(p => p.product_type === 'course');
  const downloads = products.filter(p => p.product_type === 'download');

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="admin-dashboard">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#2D4A5E] flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#1A202C]">Admin Dashboard</h1>
            <p className="text-[#64748B]">Manage your products, orders, and users</p>
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#2D4A5E]/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#2D4A5E]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">${analytics.total_revenue?.toFixed(2)}</div>
                  <div className="text-sm text-[#64748B]">Total Revenue</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#1B93A4]/10 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-[#1B93A4]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{analytics.total_orders}</div>
                  <div className="text-sm text-[#64748B]">Total Orders</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{analytics.total_users}</div>
                  <div className="text-sm text-[#64748B]">Total Users</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{analytics.total_products}</div>
                  <div className="text-sm text-[#64748B]">Total Products</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tabs */}
        <Tabs defaultValue="courses" className="w-full">
          <div className="flex justify-between items-center mb-6">
            <TabsList>
              <TabsTrigger value="courses" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Courses ({courses.length})
              </TabsTrigger>
              <TabsTrigger value="downloads" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Downloads ({downloads.length})
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Orders ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Users ({users.length})
              </TabsTrigger>
            </TabsList>

            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  <DialogDescription>Fill in the details below</DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {!editingProduct && (
                    <div>
                      <Label>Product Type</Label>
                      <Select value={productType} onValueChange={setProductType}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="course">Course</SelectItem>
                          <SelectItem value="download">Download</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="mt-1"
                      data-testid="product-title-input"
                    />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="mt-1"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(value) => setFormData({...formData, category: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(productType === 'course' ? categories.courses : categories.downloads)?.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Thumbnail URL</Label>
                    <Input
                      value={formData.thumbnail}
                      onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
                      className="mt-1"
                      placeholder="https://..."
                    />
                  </div>

                  {(productType === 'course' || editingProduct?.product_type === 'course') && (
                    <>
                      <div>
                        <Label>Video URL</Label>
                        <Input
                          value={formData.video_url}
                          onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                          className="mt-1"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label>Duration</Label>
                        <Input
                          value={formData.duration}
                          onChange={(e) => setFormData({...formData, duration: e.target.value})}
                          className="mt-1"
                          placeholder="e.g., 4 hours"
                        />
                      </div>
                    </>
                  )}

                  {(productType === 'download' || editingProduct?.product_type === 'download') && (
                    <>
                      <div>
                        <Label>File URL</Label>
                        <Input
                          value={formData.file_url}
                          onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                          className="mt-1"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label>File Type</Label>
                        <Select 
                          value={formData.file_type} 
                          onValueChange={(value) => setFormData({...formData, file_type: value})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pdf">PDF</SelectItem>
                            <SelectItem value="xlsx">XLSX</SelectItem>
                            <SelectItem value="docx">DOCX</SelectItem>
                            <SelectItem value="zip">ZIP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="featured">Featured Product</Label>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                    className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white"
                    data-testid="save-product-btn"
                  >
                    {editingProduct ? 'Update' : 'Create'} Product
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Course</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Category</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Price</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Status</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-[#64748B]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(course => (
                    <tr key={course.product_id} className="border-b border-[#E2E8F0] last:border-0">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img src={course.thumbnail || 'https://via.placeholder.com/60'} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <div className="font-medium text-[#1A202C]">{course.title}</div>
                            <div className="text-sm text-[#64748B]">{course.duration}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline">{course.category}</Badge>
                      </td>
                      <td className="px-6 py-4 font-medium">${course.price?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        {course.is_featured && <Badge className="bg-[#D4AF37] text-[#1A202C]">Featured</Badge>}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(course)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteProduct(course)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Downloads Tab */}
          <TabsContent value="downloads">
            <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Download</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Category</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Type</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Price</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-[#64748B]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {downloads.map(download => (
                    <tr key={download.product_id} className="border-b border-[#E2E8F0] last:border-0">
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#1A202C]">{download.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline">{download.category}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="secondary">{download.file_type?.toUpperCase()}</Badge>
                      </td>
                      <td className="px-6 py-4 font-medium">${download.price?.toFixed(2)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(download)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteProduct(download)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Order ID</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">User</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Items</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Total</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.order_id} className="border-b border-[#E2E8F0] last:border-0">
                      <td className="px-6 py-4 font-mono text-sm">{order.order_id}</td>
                      <td className="px-6 py-4">{order.user_id}</td>
                      <td className="px-6 py-4">{order.items?.length} item(s)</td>
                      <td className="px-6 py-4 font-medium">${order.total?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {order.payment_status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#64748B]">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">User</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Role</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#64748B]">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.user_id} className="border-b border-[#E2E8F0] last:border-0">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#2D4A5E] flex items-center justify-center text-white">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-[#1A202C]">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#64748B]">{user.email}</td>
                      <td className="px-6 py-4">
                        <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#64748B]">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPage;
